import {makeStyles, withStyles} from "@material-ui/core/styles";
import React, {useContext} from "react";
import {useApi} from "@backstage/core-plugin-api";
import {BartApiError, bartApiRef} from "../../../../../../api/api";
import CircularProgress from "@material-ui/core/CircularProgress";
import {Link, WarningPanel} from "@backstage/core-components";
import DiffViewer from "react-diff-viewer";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import InlineCode from "../../../../../core/InlineCode/InlineCode";
import Modal from "../../../../../core/Modal/Modal";
import {encode} from "../../../../../../utils/encoding";
import {WamlContext} from "./WAMLContent";

export type DiffModalProps = {
  open: boolean;
  hidden?: boolean;
  toggleModal: () => void;
}

const useDiffModalStyles = makeStyles(_theme => ({
  fmtDiffWarning: {
    fontStyle: "italic",
    fontWeight: "bold",
    fontSize: "50%",
    marginTop: "1%"
  }
}));

const WAMLDiffModal = (props: DiffModalProps) => {
  const apiClient = useApi(bartApiRef);
  const context = useContext(WamlContext);

  const classes = useDiffModalStyles();
  const {open = false, toggleModal} = props;

  const [pullRequestModalOpen, setPullRequestModalOpen] = React.useState(false);

  const togglePullRequestModal = () => {
    setPullRequestModalOpen(!pullRequestModalOpen);
  }

  const LocalSessionNotRunningTooltip = withStyles(theme => ({
    tooltip: {
      color: theme.palette.text.primary,
      margin: 16,
      padding: theme.spacing(0.5)
    },
  }))(Tooltip);

  React.useEffect(() => {
    if (open) {
      // Removed unused state variable: setSaveChangesLocallyError
    }
  }, [open])

  const [saveChangesLocallyError, setSaveChangesLocallyError] = React.useState<string>("");

  const saveChangesLocally = () => {
    if (!context.localSessionID) {
      return;
    }
    setSaveChangesLocallyError("");
    apiClient.UpdateWamlSyncDoc(context.localSessionID, encode(context.localWaml))
      .catch((err: BartApiError) => {
        setSaveChangesLocallyError(err.cause || "Failed to save changes locally.");
      })
      .finally(() => {
        // No loading state needed
      })
  }

  return (
    <Modal
      open={open}
      toggleModal={toggleModal}
      fullWidth
      title={<>
        WAML Diff
        <div className={classes.fmtDiffWarning}>Note: this might show
          more changes that are a result of running <InlineCode>bart fmt</InlineCode></div>
      </>}
      actions={
        <>
          <LocalSessionNotRunningTooltip
            title={
              context.localSessionID
                ? ""
                : "To be able to save these changes locally, run 'bart ui slos' from this repository's directory on your local machine"
            }
            disableHoverListener={!!context.localSessionID}
            placement="top"
          >
            <span>
              <Button
                onClick={saveChangesLocally}
                color={"primary"}
                disabled={!context.localSessionID}
              >
                Save Changes Locally
              </Button>
            </span>
          </LocalSessionNotRunningTooltip>
          <Button
            onClick={togglePullRequestModal}
            color={"primary"}
            disabled={!context.hasStagedChanges}
          >Create Pull Request</Button>
        </>
      }
    >
      {saveChangesLocallyError && (
        <WarningPanel
          severity="error"
          title="Local Save Error"
          defaultExpanded={true}
        >
          {saveChangesLocallyError}
        </WarningPanel>
      )}
      <DiffViewer
        oldValue={context.originalWaml}
        newValue={context.localWaml}
        splitView={true}
      />
      <WAMLDiffPullRequestModal
        open={pullRequestModalOpen}
        toggleModal={togglePullRequestModal}
        hidden={!pullRequestModalOpen}
      />
    </Modal>
  )
};

type WAMLDiffPullRequestModalProps = {
  open: boolean;
  hidden?: boolean;
  toggleModal: () => void;
}

const WAMLDiffPullRequestModal = (props: WAMLDiffPullRequestModalProps) => {
  const apiClient = useApi(bartApiRef);
  const context = useContext(WamlContext);

  const {open = false, toggleModal} = props;

  const [pullRequestURL, setPullRequestURL] = React.useState<string>("");
  const [createPullRequestError, setCreatePullRequestError] = React.useState<string>("")
  const [createPullRequestLoading, setCreatePullRequestLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!open || pullRequestURL) {
      return;
    }

    setCreatePullRequestLoading(true);
    apiClient.CreateWamlPR(context.repositoryEntity.metadata.name, context.localWaml, "feat(waml): updates from Switchboard", "Updates to the .weave.yaml file from the Switchboard").then(r => {
      setPullRequestURL(r.prUrl || "");
    }).catch((e: BartApiError) => {
      setCreatePullRequestError(e.cause);
    }).finally(() => {
      setCreatePullRequestLoading(false);
    })
  }, [open])

  return (
    <Modal
      open={open}
      toggleModal={toggleModal}
      title={createPullRequestLoading ? "Creating Pull Request" : "Pull Request Created!"}
    >
      {createPullRequestLoading && (
        <CircularProgress/>
      )}

      {createPullRequestError && (
        <WarningPanel
          severity="error"
          title={"Pull Request Creation Error"}
          defaultExpanded={true}
        >
          {createPullRequestError}
        </WarningPanel>
      )}

      {pullRequestURL && (
        <><Link to={pullRequestURL}>{pullRequestURL}</Link></>
      )}
    </Modal>
  )
}

export default WAMLDiffModal;
